import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { RecoilState, useRecoilState, useSetRecoilState } from 'recoil';
import { ReactComponent as EditIcon } from '../../../../assets/16x16/edit.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/arrow-right.svg';
import useNavigateWithEnvParam from '../../../../hooks/useNavigateWithEnvParam';
import { aiChatPanel } from '../../../../atoms/atoms';
import type {
  AgentTestChangeRowIdentifiers,
  AgentTestConfigPlan,
  AgentTestOperation,
} from '../../../../types';
import { getAgentTestChangeRowId } from '../../../../types';
import type { ToolRendererArgs } from '../../types';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { Flex, FlexColumn } from '../../../Flex';
import Button from '../../../Button';
import SvgIcon from '../../../SvgIcon';
import {
  buildNewArgsFromPlan,
  isAgentTestOperation,
  planFromToolArgs,
} from './sharedUtils';

const MAX_PREVIEW_ROWS = 3;

const PlanCard = styled(FlexColumn)<{ $status?: 'confirmed' | 'rejected' }>`
  border: 1px solid ${({ theme }) => theme.palette.common.gray17};
  border-radius: 8px;
  overflow: hidden;

  ${({ $status, theme }) =>
    $status === 'confirmed' &&
    css`
      border-color: ${theme.palette.common.green100};
    `}

  ${({ $status, theme }) =>
    $status === 'rejected' &&
    css`
      border-color: ${theme.palette.common.red100};
    `}
`;

const PlanHeader = styled(Flex)<{ $status?: 'confirmed' | 'rejected' }>`
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.palette.common.gray9};

  ${({ $status, theme }) =>
    $status === 'confirmed' &&
    css`
      background: ${theme.palette.common.green10};
    `}

  ${({ $status, theme }) =>
    $status === 'rejected' &&
    css`
      background: ${theme.palette.common.red10};
    `}
`;

const PlanBody = styled(FlexColumn)`
  padding: 12px 14px;
  gap: 16px;
`;

const ViewLink = styled(Flex)`
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.blue4};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ChangesGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, max-content);
  column-gap: 8px;
  gap: 8px;
  width: 100%;
`;

const TestIdCell = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export type PlanRendererConfig<TChange extends AgentTestChangeRowIdentifiers> =
  {
    planAtom: RecoilState<AgentTestConfigPlan<TChange> | null>;
    previewPath: string;
    returnPath: string;
    formatChangeLabel: (change: TChange) => string;
    formatChangeSummary: (
      change: TChange,
      operation: AgentTestOperation,
    ) => string;
    getHeaderLabel: (
      status: 'confirmed' | 'rejected' | undefined,
      operation: AgentTestOperation,
      count: number,
    ) => string;
    getApproveLabel: (operation: AgentTestOperation) => string;
  };

type InnerProps<TChange extends AgentTestChangeRowIdentifiers> = {
  config: PlanRendererConfig<TChange>;
  toolArgs: ToolRendererArgs;
  toolCallId: string;
  resultIsError?: boolean;
  onApproveToolUse: (
    approved: boolean,
    newArgs?: Record<string, unknown>,
  ) => void;
  isActive?: boolean;
};

function ApplyTestConfigPlanToolRendererInner<
  TChange extends AgentTestChangeRowIdentifiers,
>({
  config,
  toolArgs,
  toolCallId,
  resultIsError,
  onApproveToolUse,
  isActive,
}: InnerProps<TChange>) {
  const navigate = useNavigateWithEnvParam();
  const location = useLocation();
  const [plan, setPlan] = useRecoilState(config.planAtom);
  const setChatPanel = useSetRecoilState(aiChatPanel);
  const responseRef = useRef<'confirmed' | 'rejected' | null>(null);

  const closePreviewIfOpen = useCallback(() => {
    if (location.pathname.endsWith(config.previewPath)) {
      navigate(config.returnPath);
    }
  }, [location.pathname, navigate, config.previewPath, config.returnPath]);

  const operation = isAgentTestOperation(toolArgs?.operation)
    ? toolArgs.operation
    : 'update';
  const changes: TChange[] = Array.isArray(toolArgs?.changes)
    ? (toolArgs.changes as TChange[])
    : [];

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setPlan((prev) => {
      if (prev?.toolCallId === toolCallId) {
        return prev;
      }
      return planFromToolArgs<TChange>(toolArgs, toolCallId);
    });
  }, [isActive, setPlan, toolArgs, toolCallId]);

  useEffect(() => {
    if (
      !isActive ||
      !plan?.userResponse ||
      plan.agentAcknowledged ||
      plan.toolCallId !== toolCallId
    ) {
      return;
    }
    responseRef.current = plan.userResponse;
    if (plan.userResponse === 'confirmed') {
      onApproveToolUse(true, buildNewArgsFromPlan(plan, toolArgs));
    } else {
      onApproveToolUse(false);
    }
    setPlan((prev) => (prev ? { ...prev, agentAcknowledged: true } : prev));
  }, [isActive, onApproveToolUse, plan, setPlan, toolArgs, toolCallId]);

  const handleReviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const isReadOnly = !isActive;
      setPlan((prev) => {
        const base = planFromToolArgs<TChange>(toolArgs, toolCallId);
        if (prev?.toolCallId === toolCallId) {
          return { ...base, ...prev, ...(isReadOnly ? { isReadOnly } : {}) };
        }
        const restoredResponse: 'confirmed' | 'rejected' | undefined =
          responseRef.current ??
          (resultIsError !== undefined
            ? resultIsError
              ? 'rejected'
              : 'confirmed'
            : undefined);
        if (restoredResponse) {
          return {
            ...base,
            userResponse: restoredResponse,
            agentAcknowledged: true,
            isReadOnly,
          };
        }
        return { ...base, isReadOnly };
      });
      setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
      navigate(config.previewPath);
    },
    [
      navigate,
      setChatPanel,
      setPlan,
      toolArgs,
      toolCallId,
      resultIsError,
      isActive,
      config.previewPath,
    ],
  );

  const handleApprove = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs<TChange>(toolArgs, toolCallId);
      return { ...base, userResponse: 'confirmed' as const };
    });
    closePreviewIfOpen();
  }, [setPlan, toolArgs, toolCallId, closePreviewIfOpen]);

  const handleReject = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs<TChange>(toolArgs, toolCallId);
      return { ...base, userResponse: 'rejected' as const };
    });
    closePreviewIfOpen();
  }, [setPlan, toolArgs, toolCallId, closePreviewIfOpen]);

  const completedFromResult: 'confirmed' | 'rejected' | undefined =
    !isActive && resultIsError !== undefined
      ? resultIsError
        ? 'rejected'
        : 'confirmed'
      : undefined;

  const status: 'confirmed' | 'rejected' | undefined =
    responseRef.current ??
    (plan?.agentAcknowledged && plan.toolCallId === toolCallId
      ? plan.userResponse
      : undefined) ??
    completedFromResult;

  const previewChanges = changes.slice(0, MAX_PREVIEW_ROWS);
  const remainingCount = changes.length - previewChanges.length;

  const headerLabel = useMemo(
    () => config.getHeaderLabel(status, operation, changes.length),
    [config, status, changes.length, operation],
  );

  const approveLabel = config.getApproveLabel(operation);

  const showActions =
    isActive &&
    plan?.toolCallId === toolCallId &&
    !plan.userResponse &&
    !plan.agentAcknowledged;

  return (
    <PlanCard $status={status}>
      <PlanHeader $status={status}>
        <EditIcon width={16} height={16} />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color:
              status === 'confirmed'
                ? 'common.green100'
                : status === 'rejected'
                ? 'common.red100'
                : undefined,
          }}
        >
          {headerLabel}
        </Typography>
      </PlanHeader>
      <PlanBody>
        <ChangesGrid>
          {previewChanges.map((change) => (
            <React.Fragment key={getAgentTestChangeRowId(change)}>
              <TestIdCell>{config.formatChangeLabel(change)}</TestIdCell>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'common.gray4',
                  wordBreak: 'break-word',
                }}
              >
                {config.formatChangeSummary(change, operation)}
              </Typography>
            </React.Fragment>
          ))}
        </ChangesGrid>

        {remainingCount > 0 && (
          <Typography sx={{ fontSize: 12, color: 'common.gray4' }}>
            +{remainingCount} more
          </Typography>
        )}

        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <ViewLink onClick={handleReviewClick}>
            View full plan
            <SvgIcon
              strokeColor="blue4"
              width={10}
              style={{ marginTop: '1px' }}
            >
              <ArrowRightIcon />
            </SvgIcon>
          </ViewLink>

          {showActions && (
            <Flex sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                size="extraSmall"
                onClick={handleReject}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="extraSmall"
                onClick={handleApprove}
              >
                {approveLabel}
              </Button>
            </Flex>
          )}
        </Flex>
      </PlanBody>
    </PlanCard>
  );
}

export function createPlanApprovalRenderer<
  TChange extends AgentTestChangeRowIdentifiers,
>(config: PlanRendererConfig<TChange>) {
  return ({
    invocation,
    onApproveToolUse,
    isActive,
  }: ApprovalToolRendererProps) => (
    <ApplyTestConfigPlanToolRendererInner<TChange>
      config={config}
      toolArgs={invocation.args ?? {}}
      toolCallId={invocation.toolCallId}
      resultIsError={invocation.result?.isError}
      onApproveToolUse={onApproveToolUse}
      isActive={isActive}
    />
  );
}
